import type { MeshSurface, PlacementPosition, SurfacePoint } from '@repo/api-contracts';
import type { PhotoDimensions } from '../types';

/** 사진 맞춤으로 새 격자를 만들 때 쓰는 열·행 수. */
export const MESH_FIT_COLUMNS = 5;
export const MESH_FIT_ROWS = 4;

/** 칸 안 판정 허용치 — 칸 경계 위 점이 부동소수 오차로 양쪽 칸 모두에서 빠지지 않게 한다. */
const CELL_EPSILON = 1e-9;
/** 2차 계수가 이보다 작으면 칸을 평행사변형으로 보고 1차식으로 푼다. */
const QUADRATIC_EPSILON = 1e-12;

interface CellCorners {
  readonly p00: SurfacePoint;
  readonly p10: SurfacePoint;
  readonly p11: SurfacePoint;
  readonly p01: SurfacePoint;
}

interface CellParams {
  readonly s: number;
  readonly t: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const cross = (a: SurfacePoint, b: SurfacePoint): number => a.x * b.y - a.y * b.x;

/** 격자 점(열, 행)을 사진 px 로 바꾼다. points 는 사진 % 다. */
const gridPointPx = (
  surface: MeshSurface,
  photo: PhotoDimensions,
  column: number,
  row: number,
): SurfacePoint => {
  const point = surface.points[row * surface.columns + column];
  if (point === undefined) throw new Error(`mesh 격자 점 (${row}, ${column}) 이 없습니다.`);
  return { x: (point.x * photo.widthPx) / 100, y: (point.y * photo.heightPx) / 100 };
};

const cellCorners = (
  surface: MeshSurface,
  photo: PhotoDimensions,
  column: number,
  row: number,
): CellCorners => ({
  p00: gridPointPx(surface, photo, column, row),
  p10: gridPointPx(surface, photo, column + 1, row),
  p11: gridPointPx(surface, photo, column + 1, row + 1),
  p01: gridPointPx(surface, photo, column, row + 1),
});

const cellWidthMm = (surface: MeshSurface): number =>
  surface.realSize.widthMm / (surface.columns - 1);
const cellHeightMm = (surface: MeshSurface): number =>
  surface.realSize.heightMm / (surface.rows - 1);

const isInCell = (value: number): boolean => value >= -CELL_EPSILON && value <= 1 + CELL_EPSILON;

/**
 * 칸의 양선형 역변환을 해석으로 푼다. strict 면 s,t 가 칸 안인 해만, 아니면 |t − 0.5| 가 가장 작은 해를 범위 제한 없이 준다.
 * 2차식은 상쇄 오차를 피하려 안정형 근의 공식(q = −(k1 + sign(k1)·√disc)/2, 근 q/k2·k0/q)으로 푼다.
 */
const solveCell = (
  { p00, p10, p11, p01 }: CellCorners,
  point: SurfacePoint,
  strict: boolean,
): CellParams | null => {
  const e = { x: p10.x - p00.x, y: p10.y - p00.y };
  const f = { x: p01.x - p00.x, y: p01.y - p00.y };
  const g = { x: p00.x - p10.x + p11.x - p01.x, y: p00.y - p10.y + p11.y - p01.y };
  const h = { x: point.x - p00.x, y: point.y - p00.y };
  const k2 = cross(g, f);
  const k1 = cross(e, f) + cross(h, g);
  const k0 = cross(h, e);

  let roots: number[];
  if (Math.abs(k2) < QUADRATIC_EPSILON) {
    if (k1 === 0) return null;
    roots = [-k0 / k1];
  } else {
    let disc = k1 * k1 - 4 * k0 * k2;
    if (disc < 0) {
      if (strict) return null;
      disc = 0;
    }
    const root = Math.sqrt(disc);
    const q = -0.5 * (k1 + (k1 >= 0 ? root : -root));
    roots = q === 0 ? [0] : [q / k2, k0 / q];
  }

  const sOf = (t: number): number => {
    const dx = e.x + g.x * t;
    const dy = e.y + g.y * t;
    return Math.abs(dx) >= Math.abs(dy) ? (h.x - f.x * t) / dx : (h.y - f.y * t) / dy;
  };

  if (strict) {
    for (const t of roots) {
      if (!isInCell(t)) continue;
      const s = sOf(t);
      if (isInCell(s)) return { s, t };
    }
    return null;
  }

  let best: number | null = null;
  for (const t of roots) {
    if (best === null || Math.abs(t - 0.5) < Math.abs(best - 0.5)) best = t;
  }
  if (best === null) return null;
  const s = sOf(best);
  return Number.isFinite(s) && Number.isFinite(best) ? { s, t: best } : null;
};

const cellParamsToMm = (
  surface: MeshSurface,
  column: number,
  row: number,
  { s, t }: CellParams,
): PlacementPosition => ({
  xMm: (column + s) * cellWidthMm(surface),
  yMm: (row + t) * cellHeightMm(surface),
});

/**
 * 실측 프레임 위 점(mm)을 사진 점(px)으로 보낸다. 격자 밖 mm 는 가장자리 칸의 양선형식을 그대로 이어 외삽한다.
 */
export const meshMmToPhoto = (
  surface: MeshSurface,
  photo: PhotoDimensions,
  point: PlacementPosition,
): SurfacePoint => {
  const u = (point.xMm / surface.realSize.widthMm) * (surface.columns - 1);
  const v = (point.yMm / surface.realSize.heightMm) * (surface.rows - 1);
  const column = clamp(Math.floor(u), 0, surface.columns - 2);
  const row = clamp(Math.floor(v), 0, surface.rows - 2);
  const s = u - column;
  const t = v - row;
  const { p00, p10, p11, p01 } = cellCorners(surface, photo, column, row);
  const top = { x: p00.x + (p10.x - p00.x) * s, y: p00.y + (p10.y - p00.y) * s };
  const bottom = { x: p01.x + (p11.x - p01.x) * s, y: p01.y + (p11.y - p01.y) * s };

  return { x: top.x + (bottom.x - top.x) * t, y: top.y + (bottom.y - top.y) * t };
};

/** 사진 점(px)을 실측 프레임 위 점(mm)으로 되돌린다. 행 우선으로 처음 점을 품는 칸의 해를 주고, 격자 밖이면 null. */
export const meshPhotoToMm = (
  surface: MeshSurface,
  photo: PhotoDimensions,
  point: SurfacePoint,
): PlacementPosition | null => {
  for (let row = 0; row < surface.rows - 1; row += 1) {
    for (let column = 0; column < surface.columns - 1; column += 1) {
      const params = solveCell(cellCorners(surface, photo, column, row), point, true);
      if (params !== null) return cellParamsToMm(surface, column, row, params);
    }
  }
  return null;
};

/**
 * meshPhotoToMm 과 같지만 격자 밖 점은 null 대신, 무게중심이 가장 가까운 가장자리 칸을 밖으로 이은 해를 준다(범위 제한 없음).
 */
export const meshPhotoToMmExtrapolated = (
  surface: MeshSurface,
  photo: PhotoDimensions,
  point: SurfacePoint,
): PlacementPosition => {
  const inside = meshPhotoToMm(surface, photo, point);
  if (inside !== null) return inside;

  let nearest: { column: number; row: number; corners: CellCorners } | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let row = 0; row < surface.rows - 1; row += 1) {
    for (let column = 0; column < surface.columns - 1; column += 1) {
      const isEdge =
        row === 0 || row === surface.rows - 2 || column === 0 || column === surface.columns - 2;
      if (!isEdge) continue;
      const corners = cellCorners(surface, photo, column, row);
      const cx = (corners.p00.x + corners.p10.x + corners.p11.x + corners.p01.x) / 4;
      const cy = (corners.p00.y + corners.p10.y + corners.p11.y + corners.p01.y) / 4;
      const distance = Math.hypot(point.x - cx, point.y - cy);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = { column, row, corners };
      }
    }
  }
  if (nearest === null) throw new Error('mesh 격자에 칸이 없습니다.');

  const params = solveCell(nearest.corners, point, false) ?? { s: 0.5, t: 0.5 };
  return cellParamsToMm(surface, nearest.column, nearest.row, params);
};

/** 격자 변마다 사진 px 길이 / 칸 mm 길이 를 재어 가로·세로 최댓값(px/mm)을 준다. 렌더 해상도·핸들 크기용이다. */
export const meshScale = (surface: MeshSurface, photo: PhotoDimensions): SurfacePoint => {
  const widthMm = cellWidthMm(surface);
  const heightMm = cellHeightMm(surface);
  let x = 0;
  let y = 0;
  for (let row = 0; row < surface.rows; row += 1) {
    for (let column = 0; column < surface.columns; column += 1) {
      const here = gridPointPx(surface, photo, column, row);
      if (column < surface.columns - 1) {
        const right = gridPointPx(surface, photo, column + 1, row);
        x = Math.max(x, Math.hypot(right.x - here.x, right.y - here.y) / widthMm);
      }
      if (row < surface.rows - 1) {
        const below = gridPointPx(surface, photo, column, row + 1);
        y = Math.max(y, Math.hypot(below.x - here.x, below.y - here.y) / heightMm);
      }
    }
  }
  return { x, y };
};
